import React, {
    useCallback, useEffect, useMemo, useState
} from 'react';
import { useShallow } from 'zustand/react/shallow';

import i18n from '@nextroll/ar-i18n';
import { Alerts } from '@nextroll/ar-style-base';
import { useContainerContext } from '@nextroll/ar-container-context';

import useRequest from 'hooks/useRequest';
import usePan from 'hooks/usePan';
import useAbortController from 'hooks/useAbortController';

import { useRoflStore } from 'store/roflStore';
import { WorkflowActionService } from 'types/roflWorkflowActions';
import { getWorkflowActions } from 'api/workflowActionsApi';
import {
    WorkflowBaseType,
    IWorkflowSchemaErrors,
    IWorkflowSchemaNode,
    IWorkflowSchemaWarnings,
    WORKFLOW_BASE,
    WORKFLOW_NODE,
} from 'types/rofl';

import { ActionNode } from 'components/nodes/ActionNode/ActionNode';
import { ContactTriggerNode } from 'components/nodes/ContactTriggerNode/ContactTriggerNode';
import { AccountTriggerNode } from 'components/nodes/AccountTriggerNode/AccountTriggerNode';
import { AccountActionNode } from 'components/nodes/AccountActionNode/AccountActionNode';
import { FinishNode } from 'components/basic/NodeBase/NodeBase';

/**
 * Alert content when proceeding to the settings page without completing all nodes.
 */
const AlertIncomplete = ({ onEdit }: { onEdit: () => void }) => (
    <>
        <strong>{i18n.gettext('There is a problem with the highlighted card.')}</strong>
        <p>{i18n.gettext('You need to fix the errors in order to proceed.')}</p>
        <button className='btn btn-default' type='button' onClick={onEdit}>{i18n.gettext('Edit')}</button>
    </>
);

const determineNode = (base: WorkflowBaseType, id: string) => {
    switch (id) {
        case WORKFLOW_NODE.TRIGGER:
            return base === WORKFLOW_BASE.ACCOUNTS ? AccountTriggerNode : ContactTriggerNode;
        default:
            return base === WORKFLOW_BASE.ACCOUNTS ? AccountActionNode : ActionNode;
    }
};

interface IProps {
    initialActiveNodeId?: string;
    loadingWorkflow: boolean;
    validate?: boolean;
    setHasChanged: (arg0: boolean) => void;
    validationErrors?: IWorkflowSchemaErrors;
    validationWarnings?: IWorkflowSchemaWarnings;
}

export const NodeContainer = ({
    initialActiveNodeId,
    loadingWorkflow,
    validate,
    setHasChanged,
    validationErrors,
    validationWarnings,
}: IProps) => {
    const { advertisable: { eid: advertisableEid } = {} } = useContainerContext();
    const [activeNode, setActiveNode] = useState('');
    const [workflowActionServices, setWorkflowActionServices] = useState<WorkflowActionService[]>([]);

    const [pos, startPan, reset] = usePan();
    const transform = useMemo(() => `translate(${-pos.x}px, ${-pos.y}px)`, [pos]);
    const getSignal = useAbortController();

    const [
        base,
        contentData,
        topology,
        showRequiredError,
    ] = useRoflStore(useShallow(state => [
        state.base,
        state.contentData,
        state.topology,
        state.showRequiredError,
    ]));
    const [call] = useRequest();

    /**
     * Obtain workflow actions
     */
    useEffect(() => {
        if (!advertisableEid) {
            return;
        }

        const queryWorkflowActions = async () => {
            const abortSignal = getSignal();
            await call(async () => {
                const currentWorkflowActionServices = await getWorkflowActions(advertisableEid, abortSignal);
                if (!abortSignal.aborted) {
                    setWorkflowActionServices(currentWorkflowActionServices);
                }
            });
        };
        queryWorkflowActions();
    }, [advertisableEid, call, getSignal]);

    /**
     * If an initial active node is provided, we open the panel to show it. This is used
     * to show the trigger configuration upon landing on the page, when using templates.
     */
    useEffect(() => {
        initialActiveNodeId && setActiveNode(initialActiveNodeId);
    }, [initialActiveNodeId]);

    /**
     * TODO: in M1 topology is represented as an array of IDs. Update this logic for translating
     * the topology to the actual node structure when the workflow graph layout complexity increases.
     */
    const nodes = useMemo(() => (topology || []).map((id, index) => ({
        id,
        index,
        component: determineNode(base || WORKFLOW_BASE.CONTACTS, id),
    })), [topology, base]);

    /**
     * validate each node and show the error state accordingly
     * TODO: add logic to show error coming from validation endpoint (not property Errors)
     * We don't know yet If we're rendering trigger node or action
     * should create new interface type that includes both trigger and action as Maps - Record<string, IPropertyError>
     * + don't have the properties yet (It's being fetched inside action component)
     */
    const showError = useCallback((id: string) => {
        return contentData[id]?.hasError || (validate && contentData[id]?.isIncomplete);
    }, [validate, contentData]);

    /**
     * find the first incomplete node
     */
    const firstIncomplete = useMemo(
        () => validate && Object.entries(contentData).find(([_, data]) => data.isIncomplete)?.[0],
        [validate, contentData],
    );

    const handleApply = () => {
        /**
         * user touched somehting; turn on 'hasChange'
         */
        setHasChanged(true);

        /**
         * go to next node / panel
         */
        const curr = nodes.find(node => node.id === activeNode);
        if (curr && curr.index + 1 < nodes.length) {
            setActiveNode(nodes[curr.index + 1].id);
            return;
        }
        setActiveNode('');
    };

    return (
        <div className={`workflows-canvas-page-canvas${activeNode ? ' active' : ''}`} onMouseDown={startPan}>
            <div className='workflows-canvas-page-canvas-container' style={{ transform }}>
                {nodes.map(node => {
                    const contentDataSchemaNode = contentData[node.id].data as IWorkflowSchemaNode;
                    const action_eid = contentDataSchemaNode?.action_config?.action_eid;

                    const isTrigger = node.id === WORKFLOW_NODE.TRIGGER;
                    const errors = validationErrors?.[`${isTrigger ? 'trigger' : 'action'}_errors`];
                    const warnings = validationWarnings?.[`${isTrigger ? 'trigger' : 'action'}_warnings`];

                    return (
                        <node.component
                            key={`workflow-node-${node.id}`}
                            id={node.id}
                            active={node.id === activeNode}
                            onSelect={() => setActiveNode(node.id)}
                            onClose={() => setActiveNode('')}
                            onApply={handleApply}
                            loadingWorkflow={loadingWorkflow}
                            error={showError(node.id)}
                            warning={!!(warnings || []).length} // undefined or empty array means no warnings
                            validationErrors={errors || []}
                            validationWarnings={warnings || []}
                            workflowActionServices={workflowActionServices}
                            actionEid={action_eid}
                        />
                    );
                })}
                <FinishNode />
            </div>

            <button className='btn btn-default workflows-canvas-page-canvas-btn-recenter' type='button' onClick={reset}>
                {i18n.gettext('Re-center')}
            </button>

            {/* incomplete alert */}
            <div className='workflows-canvas-alert-container' data-testid='workflow-canvas-alert-container'>
                <Alerts
                    i18n={i18n}
                    contextualAlerts={firstIncomplete ? [{
                        type: Alerts.CONTEXTUAL_ALERT_TYPE_ERROR,
                        contentEl: (
                            <AlertIncomplete
                                onEdit={() => {
                                    setActiveNode(firstIncomplete);
                                    showRequiredError();
                                }}
                            />
                        ),
                        alertKey: 'canvas-incomplete-error-alert',
                    }] : []}
                    onDismissContextualAlert={(e: React.MouseEvent) => e.stopPropagation()}
                />
            </div>
        </div>
    );
};
